import { useMemo } from 'react';
import type { FieldPath } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { SystemPackageAppVersion } from '../../pages/create-system-package-version-page/createSystemPackageVersionPage.model';
import type {
  UseSystemPackageVersionDefinitionSection,
  UseSystemPackageVersionDefinitionSectionParams,
} from './systemPackageVersionDefinitionSection.model';
import { transformHiddenFieldsListIntoMap } from './systemPackageVersionDefinitionSection.util';

export function useSystemPackageVersionDefinitionSection({
  control,
  hiddenFields,
}: UseSystemPackageVersionDefinitionSectionParams): UseSystemPackageVersionDefinitionSection {
  const appDependencyValue = useWatch({ control, name: 'definition.appDependency' });
  const operationalSystemDependencyValue = useWatch({ control, name: 'definition.operationalSystemDependency' });
  const systemPackageDependencyValue = useWatch({ control, name: 'definition.systemPackageDependency' });

  const disabledFields = useMemo<FieldPath<SystemPackageAppVersion>[]>(
    () => [
      ...(operationalSystemDependencyValue
        ? []
        : ['definition.minimumOSVersion' as FieldPath<SystemPackageAppVersion>]),
      ...(appDependencyValue ? [] : ['definition.minimumAppVersion' as FieldPath<SystemPackageAppVersion>]),
      ...(systemPackageDependencyValue
        ? []
        : ['definition.minimumSystemPackageVersion' as FieldPath<SystemPackageAppVersion>]),
    ],
    [appDependencyValue, operationalSystemDependencyValue, systemPackageDependencyValue],
  );

  const hiddenFieldsMap = useMemo(() => transformHiddenFieldsListIntoMap(hiddenFields), [hiddenFields]);

  return useMemo(
    () => ({
      disabledFields,
      hiddenFieldsMap,
    }),
    [disabledFields, hiddenFieldsMap],
  );
}
